import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import { useUserHasAccess } from "../../../routes/roleOnly";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";
import { ProjectEventDialog } from "../../../components/dashboard/projects/projectEventDialog";
import { ProjectSelector } from "../../../components/utils/moduleSelectors/projectSelector";
import axios from "../../../api/axios";
import { fetchHoursResourcePlan } from "../../../api/hour";
import {
  format,
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  startOfWeek,
  endOfWeek,
  getWeek,
} from "date-fns";
import { nb } from "date-fns/locale";
import { PlusIcon } from "@heroicons/react/24/outline";
import { CrewProjectPanel } from "../../../components/dashboard/projects/crewProjectPanel";

// Example data for development/testing
const EXAMPLE_PROJECT_EVENTS = {
  data: [
    {
      id: 1,
      title: "Installation Phase 1",
      description: "Initial installation of equipment at customer site",
      start_date: "2024-01-15",
      end_date: "2024-01-20",
      project_id: 101,
      work_order_id: 501,
      activity_id: null,
      created_at: "2024-01-10T10:00:00Z",
      updated_at: "2024-01-10T10:00:00Z",
      project: {
        id: 101,
        displayTitle: "101 - Customer Installation Project",
        name: "Customer Installation Project",
        project_number: 101
      },
      work_order: {
        id: 501,
        displayTitle: "501 - Equipment Installation",
        title: "Equipment Installation",
        number: 501,
        status: "ongoing"
      },
      activity: null,
      resources: [
        {
          id: 201,
          user_id: 10,
          user: {
            id: 10,
            displayTitle: "John Doe",
            name: "John Doe"
          },
          start_time: "2024-01-15T08:00:00Z",
          end_time: "2024-01-15T16:00:00Z",
          title: "Installation Phase 1",
          description: "Initial installation of equipment at customer site",
          project_id: 101,
          status: "pending",
          active: true
        },
        {
          id: 202,
          user_id: 11,
          user: {
            id: 11,
            displayTitle: "Jane Smith",
            name: "Jane Smith"
          },
          start_time: "2024-01-16T08:00:00Z",
          end_time: "2024-01-16T16:00:00Z",
          title: "Installation Phase 1",
          description: "Initial installation of equipment at customer site",
          project_id: 101,
          status: "pending",
          active: true
        }
      ]
    },
    {
      id: 2,
      title: "Maintenance Check",
      description: "Regular maintenance check for installed equipment",
      start_date: "2024-01-18",
      end_date: "2024-01-18",
      project_id: 101,
      work_order_id: null,
      activity_id: 301,
      created_at: "2024-01-12T14:30:00Z",
      updated_at: "2024-01-12T14:30:00Z",
      project: {
        id: 101,
        displayTitle: "101 - Customer Installation Project",
        name: "Customer Installation Project",
        project_number: 101
      },
      work_order: null,
      activity: {
        id: 301,
        name: "Maintenance",
        title: "Maintenance",
        number: "M-001"
      },
      resources: [
        {
          id: 204,
          user_id: 12,
          user: {
            id: 12,
            displayTitle: "Bob Johnson",
            name: "Bob Johnson"
          },
          start_time: "2024-01-18T09:00:00Z",
          end_time: "2024-01-18T15:00:00Z",
          title: "Maintenance Check",
          description: "Regular maintenance check for installed equipment",
          project_id: 101,
          status: "pending",
          active: true
        }
      ]
    },
    {
      id: 3,
      title: "Site Survey",
      description: "Initial site survey for new project",
      start_date: "2024-01-20",
      end_date: "2024-01-22",
      project_id: 102,
      work_order_id: null,
      activity_id: null,
      project_activity_id: 9001,
      created_at: "2024-01-08T09:15:00Z",
      updated_at: "2024-01-08T09:15:00Z",
      project: {
        id: 102,
        displayTitle: "102 - New Office Building",
        name: "New Office Building",
        project_number: 102
      },
      work_order: null,
      activity: null,
      project_activity: {
        id: 9001,
        display_title: "Site preparation",
        title: "Site preparation",
      },
      resources: [
        {
          id: 205,
          user_id: 10,
          user: {
            id: 10,
            displayTitle: "John Doe",
            name: "John Doe"
          },
          start_time: "2024-01-20T08:00:00Z",
          end_time: "2024-01-20T16:00:00Z",
          title: "Site Survey",
          description: "Initial site survey for new project",
          project_id: 102,
          status: "pending",
          active: true
        },
        {
          id: 206,
          user_id: 13,
          user: {
            id: 13,
            displayTitle: "Alice Williams",
            name: "Alice Williams"
          },
          start_time: "2024-01-21T08:00:00Z",
          end_time: "2024-01-21T16:00:00Z",
          title: "Site Survey",
          description: "Initial site survey for new project",
          project_id: 102,
          status: "pending",
          active: true
        }
      ]
    },
    {
      id: 4,
      title: "Equipment Testing",
      description: "Testing phase for new equipment",
      start_date: "2024-01-25",
      end_date: "2024-01-26",
      project_id: 101,
      work_order_id: 502,
      activity_id: 302,
      created_at: "2024-01-14T11:20:00Z",
      updated_at: "2024-01-14T11:20:00Z",
      project: {
        id: 101,
        displayTitle: "101 - Customer Installation Project",
        name: "Customer Installation Project",
        project_number: 101
      },
      work_order: {
        id: 502,
        displayTitle: "502 - Equipment Testing",
        title: "Equipment Testing",
        number: 502,
        status: "draft"
      },
      activity: {
        id: 302,
        name: "Testing",
        title: "Testing",
        number: "T-001"
      },
      resources: [
        {
          id: 208,
          user_id: 11,
          user: {
            id: 11,
            displayTitle: "Jane Smith",
            name: "Jane Smith"
          },
          start_time: "2024-01-25T08:00:00Z",
          end_time: "2024-01-25T16:00:00Z",
          title: "Equipment Testing",
          description: "Testing phase for new equipment",
          project_id: 101,
          status: "pending",
          active: true
        }
      ]
    }
  ],
  meta: {
    total: 4,
    page: 1,
    per_page: 50,
    total_pages: 1
  }
};

// Set to true to use example data instead of API calls
// Change this to true to see example data displayed
const USE_EXAMPLE_DATA = false; // Set to false to use real API

const hoursBetween = (start, end) => {
  if (!start || !end || end <= start) return 0;
  return Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60));
};

const resourceHoursForDay = (resource, dayKey) => {
  const start = resource?.start_time ? new Date(resource.start_time) : null;
  const end = resource?.end_time ? new Date(resource.end_time) : null;
  if (!start) return 0;

  const startKey = format(start, "yyyy-MM-dd");
  const endKey = end ? format(end, "yyyy-MM-dd") : startKey;
  if (dayKey < startKey || dayKey > endKey) return 0;

  if (end && startKey === endKey) return hoursBetween(start, end);
  return 8;
};

const resourceSpansDay = (resource, dayKey) => resourceHoursForDay(resource, dayKey) > 0;

const getPeriodRangeForDate = (dateObj, type) => {
  switch (type) {
    case "month":
      return { from: startOfMonth(dateObj), to: endOfMonth(dateObj) };
    case "quarter":
      return { from: startOfQuarter(dateObj), to: endOfQuarter(dateObj) };
    case "year":
      return { from: startOfYear(dateObj), to: endOfYear(dateObj) };
    default:
      return { from: startOfMonth(dateObj), to: endOfMonth(dateObj) };
  }
};

const ProjectPlanContent = () => {
  const { t } = useTranslation();
  const { project_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedProject, setSelectedProject] = useState(null);
  const [periodType, setPeriodType] = useState(searchParams.get("period") || "month");
  const [baseDate, setBaseDate] = useState(
    searchParams.get("date") || new Date().toISOString().substring(0, 10)
  );
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resourcePlan, setResourcePlan] = useState(null);
  const [resourcePlanLoading, setResourcePlanLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);
  
  // Drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
  const scrollContainerRef = useRef(null);
  
  // Right-click context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedCells, setSelectedCells] = useState([]);

  // Drag-to-scroll handlers
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return; // Only handle left mouse button
    if (!scrollContainerRef.current) return;
    
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setScrollStart({
      x: scrollContainerRef.current.scrollLeft,
      y: scrollContainerRef.current.scrollTop,
    });
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !scrollContainerRef.current) return;
    
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    scrollContainerRef.current.scrollLeft = scrollStart.x - deltaX;
    scrollContainerRef.current.scrollTop = scrollStart.y - deltaY;
  }, [isDragging, dragStart, scrollStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Right-click context menu handlers
  const handleContextMenu = useCallback((e, day, activityKey = null) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      day: format(day, "yyyy-MM-dd"),
      activityKey,
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleAddEventFromContextMenu = useCallback(() => {
    if (contextMenu) {
      handleNewEvent(contextMenu.day);
      handleCloseContextMenu();
    }
  }, [contextMenu]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu) {
        handleCloseContextMenu();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [contextMenu, handleCloseContextMenu]);

  // Initialize date range from period type
  useEffect(() => {
    const range = getPeriodRangeForDate(new Date(baseDate), periodType);
    setDateFrom(format(range.from, "yyyy-MM-dd"));
    setDateTo(format(range.to, "yyyy-MM-dd"));
  }, [baseDate, periodType]);

  // Load project if project_id is in URL
  useEffect(() => {
    if (project_id) {
      axios({ method: "get", url: `/projects/${project_id}` })
        .then((res) => {
          if (res.data) {
            setSelectedProject(res.data);
          }
        })
        .catch((err) => {
          console.error("Error loading project:", err);
        });
    }
  }, [project_id]);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      // Use example data if enabled
      if (USE_EXAMPLE_DATA) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Filter example data based on selected project and date range
        let filteredData = EXAMPLE_PROJECT_EVENTS.data;
        
        // Filter by project if selected
        if (selectedProject?.id) {
          filteredData = filteredData.filter(event => event.project_id === selectedProject.id);
        }
        
        // Filter by date range
        if (dateFrom && dateTo) {
          const fromDate = new Date(dateFrom);
          const toDate = new Date(dateTo);
          filteredData = filteredData.filter(event => {
            const eventStart = event.start_date ? new Date(event.start_date) : null;
            const eventEnd = event.end_date ? new Date(event.end_date) : null;
            
            if (eventStart && eventEnd) {
              // Event overlaps with date range
              return eventStart <= toDate && eventEnd >= fromDate;
            } else if (eventStart) {
              return eventStart >= fromDate && eventStart <= toDate;
            }
            return false;
          });
        }
        
        setEvents(filteredData);
        setLoading(false);
        return;
      }

      // Real API call
      const params = {
        date_from: dateFrom,
        date_to: dateTo,
        fields: "*",
      };

      // Only add project_id filter if a specific project is selected
      if (selectedProject?.id) {
        params.project_id = selectedProject.id;
      }

      const response = await axios({
        method: "get",
        url: "/project_events",
        params,
      });

      if (response.data?.data) {
        setEvents(response.data.data);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error("Error fetching project events:", error);
      // Fallback to example data only if explicitly enabled
      if (USE_EXAMPLE_DATA) {
        console.log("Using example data due to API error");
        let filteredData = EXAMPLE_PROJECT_EVENTS.data;
        if (selectedProject?.id) {
          filteredData = filteredData.filter(event => event.project_id === selectedProject.id);
        }
        setEvents(filteredData);
      } else {
        setEvents([]);
        // Show error message to user
        console.error("Failed to load project events:", error.response?.data || error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedProject?.id, dateFrom, dateTo]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const fetchResourcePlan = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      setResourcePlan(null);
      return;
    }

    setResourcePlanLoading(true);
    try {
      const response = await fetchHoursResourcePlan({
        date_from: dateFrom,
        date_to: dateTo,
      });
      setResourcePlan(response?.data?.data || null);
    } catch (error) {
      console.error("Error fetching resource plan:", error);
      setResourcePlan(null);
    } finally {
      setResourcePlanLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchResourcePlan();
  }, [fetchResourcePlan]);

  const handlePeriodChange = ({ periodType: newType, from, to }) => {
    setPeriodType(newType || periodType);
    setDateFrom(from);
    setDateTo(to);
    setSearchParams({
      period: newType || periodType,
      date: baseDate,
    });
  };

  const handleNewEvent = (date = null) => {
    setSelectedEvent(null);
    setPrefillDate(date);
    setDialogOpen(true);
  };

  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setPrefillDate(null);
    setDialogOpen(true);
  };

  const handleEventSaved = (savedEvent) => {
    fetchEvents();
  };

  const resourcePlanByDate = useMemo(() => {
    const map = new Map();
    (resourcePlan?.days || []).forEach((day) => {
      map.set(day.date, day);
    });
    return map;
  }, [resourcePlan]);

  const projectCapacityByDate = useMemo(() => {
    const map = new Map();

    events.forEach((event) => {
      const eventStartKey = event.start_date ? format(new Date(event.start_date), "yyyy-MM-dd") : null;
      const eventEndKey = event.end_date ? format(new Date(event.end_date), "yyyy-MM-dd") : eventStartKey;
      if (!eventStartKey) return;

      const eventStart = new Date(eventStartKey);
      const eventEnd = new Date(eventEndKey);
      eachDayOfInterval({ start: eventStart, end: eventEnd }).forEach((day) => {
        const dKey = format(day, "yyyy-MM-dd");
        const current = map.get(dKey) || {
          events: 0,
          assignedResources: 0,
          assignedHours: 0,
        };

        current.events += 1;
        (event.resources || []).forEach((resource) => {
          const hours = resourceHoursForDay(resource, dKey);
          if (hours > 0) {
            current.assignedResources += 1;
            current.assignedHours += hours;
          }
        });

        map.set(dKey, current);
      });
    });

    return map;
  }, [events]);

  const capacityDays = useMemo(() => {
    if (!dateFrom || !dateTo) return [];

    return eachDayOfInterval({
      start: new Date(dateFrom),
      end: new Date(dateTo),
    }).map((day) => {
      const dKey = format(day, "yyyy-MM-dd");
      const resourceDay = resourcePlanByDate.get(dKey);
      const projectDay = projectCapacityByDate.get(dKey) || {};
      const available = resourceDay?.stats?.available || 0;
      const scheduledHours = resourceDay?.stats?.scheduled_hours || 0;
      const assignedResources = projectDay.assignedResources || 0;
      const assignedHours = projectDay.assignedHours || 0;

      return {
        date: dKey,
        available,
        scheduledHours,
        assignedResources,
        assignedHours,
        events: projectDay.events || 0,
        freeResources: Math.max(available - assignedResources, 0),
        freeHours: Math.max(scheduledHours - assignedHours, 0),
      };
    });
  }, [dateFrom, dateTo, projectCapacityByDate, resourcePlanByDate]);

  const capacityTotals = useMemo(
    () =>
      capacityDays.reduce(
        (acc, day) => ({
          events: acc.events + day.events,
          available: acc.available + day.available,
          assignedResources: acc.assignedResources + day.assignedResources,
          scheduledHours: acc.scheduledHours + day.scheduledHours,
          assignedHours: acc.assignedHours + day.assignedHours,
          freeHours: acc.freeHours + day.freeHours,
        }),
        {
          events: 0,
          available: 0,
          assignedResources: 0,
          scheduledHours: 0,
          assignedHours: 0,
          freeHours: 0,
        }
      ),
    [capacityDays]
  );

  const renderPeriodTable = () => {
    if (!dateFrom || !dateTo) return null;

    const start = new Date(dateFrom);
    const end = new Date(dateTo);
    const days = eachDayOfInterval({ start, end });

    // Group events by project, then by activity/work_order, then show resources
    const projectMap = new Map();
    events.forEach((event) => {
      const projectId = event.project_id || selectedProject?.id || "none";
      const projectName = event.project?.displayTitle || selectedProject?.displayTitle || t("No project");
      
      if (!projectMap.has(projectId)) {
        projectMap.set(projectId, {
          projectId,
          projectName,
          activityRows: new Map(), // Map of activity/work_order rows
        });
      }
      
      const project = projectMap.get(projectId);
      // Always create a unique row for each event to ensure all are displayed
      // Use event ID to ensure uniqueness, but keep work_order/activity info for display
      const rowKey = `event_${event.id}`;
      
      // Create new row for this event
      const rowType = event.work_order_id
        ? "work_order"
        : event.project_activity_id
          ? "project_activity"
          : event.activity_id
            ? "activity"
            : "event";
      const pa = event.project_activity;
      const rowName =
        event.work_order?.displayTitle ||
        pa?.display_title ||
        pa?.title ||
        event.activity?.name ||
        event.activity?.displayTitle ||
        event.title ||
        t("No activity");
      project.activityRows.set(rowKey, {
        key: rowKey,
        type: rowType,
        name: rowName,
        event: event, // Store the event for resources
        resources: event.resources || [],
      });
    });

    // Calculate total rows per project (for rowspan)
    Array.from(projectMap.values()).forEach((project) => {
      let totalRows = 0;
      Array.from(project.activityRows.values()).forEach((activityRow) => {
        // Count activity row + resource rows (only if resources exist)
        totalRows += 1 + activityRow.resources.length; // Only count actual resource rows
      });
      project.totalRows = totalRows;
    });

    // Flatten into rows: project header, then activity/work_order rows, then resource rows
    const tableRows = [];
    Array.from(projectMap.values()).forEach((project) => {
      const activityRows = Array.from(project.activityRows.values());
      
      if (activityRows.length > 0) {
        // First row: project header + first activity
        const firstActivity = activityRows[0];
        
        tableRows.push({
          type: "project_with_activity",
          projectId: project.projectId,
          projectName: project.projectName,
          rowspan: project.totalRows,
          activityKey: firstActivity.key,
          activityName: firstActivity.name,
          activityType: firstActivity.type,
          event: firstActivity.event,
          resources: firstActivity.resources,
          isFirstResourceRow: true,
        });
        
        // Add resource rows for first activity (only if resources exist)
        firstActivity.resources.forEach((resource, rIdx) => {
          tableRows.push({
            type: "resource_row",
            projectId: project.projectId,
            activityKey: firstActivity.key,
            resource: resource,
            resourceIndex: rIdx,
            parentEvent: firstActivity.event, // Store parent ProjectEvent
          });
        });
        
        // Remaining activity rows
        activityRows.slice(1).forEach((activityRow) => {
          tableRows.push({
            type: "activity_row",
            projectId: project.projectId,
            activityKey: activityRow.key,
            activityName: activityRow.name,
            activityType: activityRow.type,
            event: activityRow.event,
            resources: activityRow.resources,
          });
          
          // Add resource rows for this activity (only if resources exist)
          activityRow.resources.forEach((resource, rIdx) => {
            tableRows.push({
              type: "resource_row",
              projectId: project.projectId,
              activityKey: activityRow.key,
              resource: resource,
              resourceIndex: rIdx,
              parentEvent: activityRow.event, // Store parent ProjectEvent
            });
          });
        });
      } else {
        // Project with no activities
        tableRows.push({
          type: "project_only",
          projectId: project.projectId,
          projectName: project.projectName,
        });
      }
    });

    // Calculate column totals (count resources per day)
    const columnTotals = days.map((day) => {
      const dKey = format(day, "yyyy-MM-dd");
      let count = 0;
      tableRows.forEach((row) => {
        if (row.type === "resource_row" && row.resource) {
          if (resourceSpansDay(row.resource, dKey)) count++;
        }
      });
      return count;
    });

    // Group days by year and week for header display
    const yearWeekMap = new Map();
    days.forEach((day) => {
      const year = format(day, "yyyy");
      const weekStart = startOfWeek(day, { weekStartsOn: 1 });
      const weekKey = `${year}-W${getWeek(day, { weekStartsOn: 1 })}`;
      
      if (!yearWeekMap.has(year)) {
        yearWeekMap.set(year, new Map());
      }
      const weekMap = yearWeekMap.get(year);
      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, { start: weekStart, days: [] });
      }
      weekMap.get(weekKey).days.push(day);
    });

    // Calculate column spans for year and week headers
    const yearSpans = [];
    const weekSpans = [];
    let dayIndex = 0;
    
    Array.from(yearWeekMap.entries()).forEach(([year, weekMap]) => {
      const yearDayCount = Array.from(weekMap.values()).reduce((sum, week) => sum + week.days.length, 0);
      yearSpans.push({ year, span: yearDayCount, startIndex: dayIndex });
      
      Array.from(weekMap.values()).forEach((week) => {
        weekSpans.push({ week: getWeek(week.days[0], { weekStartsOn: 1 }), span: week.days.length, startIndex: dayIndex });
        dayIndex += week.days.length;
      });
    });

    return (
      <div 
        ref={scrollContainerRef}
        className={`overflow-x-auto ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            {/* Year row */}
            <tr>
              <th rowSpan={3} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-48 border-r-2 border-gray-300 bg-gray-50 sticky left-0 z-50 shadow-[4px_0_6px_rgba(0,0,0,0.15)]" style={{ backgroundColor: '#f9fafb', borderRight: '2px solid #d1d5db' }}>
                {t("project.plan.project")}
              </th>
              <th rowSpan={3} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border-r-2 border-gray-300 bg-gray-50 sticky z-50 shadow-[4px_0_6px_rgba(0,0,0,0.15)]" style={{ backgroundColor: '#f9fafb', borderLeft: 'none', left: '120px' }}>
                {t("project.activities.title")} / {t("project.event.work_order")} / {t("project.event.resources")}
              </th>
              {yearSpans.map(({ year, span, startIndex }) => (
                <th
                  key={`year-${year}-${startIndex}`}
                  colSpan={span}
                  className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-b border-gray-300"
                >
                  {year}
                </th>
              ))}
            </tr>
            {/* Week row */}
            <tr>
              {weekSpans.map(({ week, span, startIndex }, index) => (
                <th
                  key={`week-${week}-${startIndex}`}
                  colSpan={span}
                  className={`px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 ${
                    index === 0 ? 'border-l-2 border-gray-400' : ''
                  } border-r-2 border-gray-400`}
                >
                  {t("project.plan.week")} {week}
                </th>
              ))}
            </tr>
            {/* Day row */}
            <tr>
              {days.map((day, dayIndex) => {
                // Check if this is the start of a week (Monday)
                const isWeekStart = day.getDay() === 1; // Monday = 1
                // Check if this is the end of a week (Sunday) or last day
                const isWeekEnd = day.getDay() === 0 || dayIndex === days.length - 1; // Sunday = 0
                // Check if weekend (Saturday = 6, Sunday = 0)
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                // Check if today
                const today = new Date();
                const dKey = format(day, "yyyy-MM-dd");
                const isToday = dKey === format(today, "yyyy-MM-dd");
                const capacity = capacityDays.find((item) => item.date === dKey);
                
                // Day names in Norwegian
                const dayNames = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];
                const dayName = dayNames[day.getDay()];
                
                return (
                  <th
                    key={day.toISOString()}
                    className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider min-w-[60px] ${
                      isWeekStart ? 'border-l-2 border-gray-400' : ''
                    } ${isWeekEnd ? 'border-r-2 border-gray-400' : ''} ${
                      isWeekend ? 'text-red-600 font-semibold' : 'text-gray-500'
                    } ${isToday ? 'bg-blue-100 border-2 border-blue-500' : ''}`}
                  >
                    <div className="flex flex-col">
                      <span className="text-[10px]">{dayName}</span>
                      <span className={`text-sm font-semibold ${isToday ? 'text-blue-700' : ''}`}>{format(day, "d")}</span>
                      {capacity ? (
                        <span
                          className={`mt-1 rounded px-1 py-0.5 text-[10px] ${
                            capacity.freeResources > 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                          }`}
                          title={`${t("project.plan.resource_capacity")}: ${capacity.assignedResources}/${capacity.available}`}
                        >
                          {capacity.assignedResources}/{capacity.available}
                        </span>
                      ) : null}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableRows.length === 0 ? (
              <tr>
                <td
                  className="px-4 py-6 text-sm text-gray-500"
                  colSpan={days.length + 2}
                >
                  {t("project.plan.no_events")}
                </td>
              </tr>
            ) : (
              tableRows.map((row, idx) => {
                if (row.type === "project_with_activity") {
                  // First row: Project header + Activity/WorkOrder
                  return (
                    <tr key={`${row.projectId}-${row.activityKey}`} className="hover:bg-gray-50">
                      <td
                        className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap border-r-2 border-gray-300 bg-gray-50 sticky left-0 z-40 shadow-[4px_0_6px_rgba(0,0,0,0.15)]"
                        style={{ backgroundColor: '#f9fafb', borderRight: '2px solid #d1d5db' }}
                        rowSpan={row.rowspan}
                      >
                        {row.projectName}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap sticky z-40 bg-white border-r-2 border-gray-300 shadow-[4px_0_6px_rgba(0,0,0,0.15)]" style={{ backgroundColor: 'white', borderLeft: 'none', left: '120px' }}>
                        <div className="flex items-center space-x-2 pl-4">
                          <span className="text-gray-400">└</span>
                          <span className="font-medium">{row.activityName}</span>
                        </div>
                      </td>
                      {days.map((day, dayIndex) => {
                        const dKey = format(day, "yyyy-MM-dd");
                        // Check if this is the start/end of a week
                        const isWeekStart = day.getDay() === 1; // Monday = 1
                        const isWeekEnd = day.getDay() === 0 || dayIndex === days.length - 1; // Sunday = 0
                        // Check if today
                        const today = new Date();
                        const isToday = dKey === format(today, "yyyy-MM-dd");
                        
                        // Show event badge on activity row if event spans this day
                        const event = row.event;
                        const eventStart = event?.start_date ? new Date(event.start_date) : null;
                        const eventEnd = event?.end_date ? new Date(event.end_date) : null;
                        const dayDate = new Date(dKey);
                        const eventSpansDay = eventStart && eventEnd 
                          ? (dayDate >= eventStart && dayDate <= eventEnd)
                          : eventStart 
                          ? format(eventStart, "yyyy-MM-dd") === dKey
                          : false;

                        return (
                          <td
                            key={dKey}
                            className={`px-2 py-2 text-center text-sm ${
                              isWeekStart ? 'border-l-2 border-gray-400' : ''
                            } ${isWeekEnd ? 'border-r-2 border-gray-400' : ''} ${
                              isToday ? 'bg-blue-50 border-l-2 border-r-2 border-blue-400' : ''
                            }`}
                            onContextMenu={(e) => handleContextMenu(e, day, row.activityKey)}
                          >
                            {eventSpansDay && (
                              <div
                                className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs cursor-pointer hover:bg-blue-200 transition-colors inline-block"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditEvent(event);
                                }}
                                onContextMenu={(e) => {
                                  e.stopPropagation();
                                  handleContextMenu(e, day, row.activityKey);
                                }}
                                title={event.title || event.description}
                              >
                                {event.title 
                                  ? (event.title.length > 6
                                      ? event.title.substring(0, 6) + "..."
                                      : event.title)
                                  : "•"}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                } else if (row.type === "activity_row") {
                  // Activity/WorkOrder row (without project header)
                  return (
                    <tr key={`${row.activityKey}-${idx}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap sticky z-40 bg-white border-r-2 border-gray-300 shadow-[4px_0_6px_rgba(0,0,0,0.15)]" style={{ backgroundColor: 'white', borderLeft: 'none', left: '120px' }}>
                        <div className="flex items-center space-x-2 pl-4">
                          <span className="text-gray-400">└</span>
                          <span className="font-medium">{row.activityName}</span>
                        </div>
                      </td>
                      {days.map((day, dayIndex) => {
                        const dKey = format(day, "yyyy-MM-dd");
                        // Check if this is the start/end of a week
                        const isWeekStart = day.getDay() === 1; // Monday = 1
                        const isWeekEnd = day.getDay() === 0 || dayIndex === days.length - 1; // Sunday = 0
                        // Check if today
                        const today = new Date();
                        const isToday = dKey === format(today, "yyyy-MM-dd");
                        
                        const event = row.event;
                        const eventStart = event?.start_date ? new Date(event.start_date) : null;
                        const eventEnd = event?.end_date ? new Date(event.end_date) : null;
                        const dayDate = new Date(dKey);
                        const eventSpansDay = eventStart && eventEnd
                          ? (dayDate >= eventStart && dayDate <= eventEnd)
                          : eventStart
                          ? format(eventStart, "yyyy-MM-dd") === dKey
                          : false;

                        return (
                          <td
                            key={dKey}
                            className={`px-2 py-2 text-center text-sm ${
                              isWeekStart ? 'border-l-2 border-gray-400' : ''
                            } ${isWeekEnd ? 'border-r-2 border-gray-400' : ''} ${
                              isToday ? 'bg-blue-50 border-l-2 border-r-2 border-blue-400' : ''
                            }`}
                            onContextMenu={(e) => handleContextMenu(e, day, row.activityKey)}
                          >
                            {eventSpansDay && (
                              <div
                                className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs cursor-pointer hover:bg-blue-200 transition-colors inline-block"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditEvent(event);
                                }}
                                onContextMenu={(e) => {
                                  e.stopPropagation();
                                  handleContextMenu(e, day, row.activityKey);
                                }}
                                title={event.title || event.description}
                              >
                                {event.title 
                                  ? (event.title.length > 6
                                      ? event.title.substring(0, 6) + "..."
                                      : event.title)
                                  : "•"}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                } else if (row.type === "resource_row") {
                  // Resource row (employee)
                  return (
                    <tr key={`resource-${row.activityKey}-${row.resourceIndex}-${idx}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap sticky z-40 bg-white border-r-2 border-gray-300 shadow-[4px_0_6px_rgba(0,0,0,0.15)]" style={{ backgroundColor: 'white', borderLeft: 'none', left: '120px' }}>
                        <div className="flex items-center space-x-2 pl-12">
                          <span className="text-gray-300">└</span>
                          <span className="text-gray-500">
                            {row.resource?.user?.displayTitle || row.resource?.user?.name || ""}
                          </span>
                        </div>
                      </td>
                      {days.map((day, dayIndex) => {
                        const dKey = format(day, "yyyy-MM-dd");
                        // Check if this is the start/end of a week
                        const isWeekStart = day.getDay() === 1; // Monday = 1
                        const isWeekEnd = day.getDay() === 0 || dayIndex === days.length - 1; // Sunday = 0
                        // Check if today
                        const today = new Date();
                        const isToday = dKey === format(today, "yyyy-MM-dd");
                        const resourceIsPlanned = resourceSpansDay(row.resource, dKey);

                        return (
                          <td
                            key={dKey}
                            className={`px-2 py-2 text-center text-sm ${
                              isWeekStart ? 'border-l-2 border-gray-400' : ''
                            } ${isWeekEnd ? 'border-r-2 border-gray-400' : ''} ${
                              isToday ? 'bg-blue-50 border-l-2 border-r-2 border-blue-400' : ''
                            }`}
                            onContextMenu={(e) => handleContextMenu(e, day, row.activityKey)}
                          >
                            {resourceIsPlanned ? (
                              <div
                                className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs cursor-pointer hover:bg-green-200 transition-colors inline-block"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Edit the parent ProjectEvent when clicking on a resource
                                  if (row.parentEvent) {
                                    handleEditEvent(row.parentEvent);
                                  }
                                }}
                                onContextMenu={(e) => {
                                  e.stopPropagation();
                                  handleContextMenu(e, day, row.activityKey);
                                }}
                                title={`${row.resource.user?.displayTitle || row.resource.user?.name || "Resource"}: ${row.resource.start_time ? format(new Date(row.resource.start_time), "HH:mm") : ""} - ${row.resource.end_time ? format(new Date(row.resource.end_time), "HH:mm") : ""}`}
                              >
                                {row.resource.user?.displayTitle?.substring(0, 6) || row.resource.user?.name?.substring(0, 6) || "•"}
                              </div>
                            ) : (
                              <span
                                className="text-gray-300 cursor-pointer hover:text-gray-400 text-lg font-semibold"
                                onClick={() => {
                                  handleNewEvent(format(day, "yyyy-MM-dd"));
                                }}
                                onContextMenu={(e) => {
                                  e.stopPropagation();
                                  handleContextMenu(e, day, row.activityKey);
                                }}
                                title={t("project.event.create")}
                              >
                                +
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                } else if (row.type === "project_only") {
                  // Project with no activities
                  return (
                    <tr key={`project-only-${row.projectId}`} className="bg-gray-50">
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap border-r-2 border-gray-300 sticky left-0 z-40 bg-gray-50 shadow-[4px_0_6px_rgba(0,0,0,0.15)]" style={{ backgroundColor: '#f9fafb', borderRight: '2px solid #d1d5db' }}>
                        {row.projectName}
                      </td>
                      <td
                        className="px-4 py-6 text-sm text-gray-500 sticky z-40 bg-white border-r-2 border-gray-300 shadow-[4px_0_6px_rgba(0,0,0,0.15)]"
                        style={{ backgroundColor: 'white', borderLeft: 'none', left: '194px' }}
                        colSpan={days.length + 1}
                      >
                        {t("project.plan.no_events")}
                      </td>
                    </tr>
                  );
                }
                return null;
              })
            )}
          </tbody>
          {tableRows.length > 0 && (
            <tfoot className="bg-gray-50 sticky bottom-0 z-30">
              <tr>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap sticky left-0 z-40 bg-gray-50 shadow-[4px_0_6px_rgba(0,0,0,0.15)]" style={{ backgroundColor: '#f9fafb' }} colSpan={2}>
                  {t("project.plan.total")}
                </th>
                {columnTotals.map((count, i) => {
                  const day = days[i];
                  const isWeekStart = day?.getDay() === 1; // Monday = 1
                  const isWeekEnd = day?.getDay() === 0 || i === days.length - 1; // Sunday = 0
                  // Check if today
                  const today = new Date();
                  const dKey = day ? format(day, "yyyy-MM-dd") : "";
                  const isToday = dKey === format(today, "yyyy-MM-dd");
                  
                  return (
                    <th
                      key={i}
                      className={`px-2 py-3 text-center text-xs font-semibold text-gray-700 ${
                        isWeekStart ? 'border-l-2 border-gray-400' : ''
                      } ${isWeekEnd ? 'border-r-2 border-gray-400' : ''} ${
                        isToday ? 'bg-blue-50 border-l-2 border-r-2 border-blue-400' : ''
                      }`}
                    >
                      {count > 0 ? count : ""}
                    </th>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  return (
    <DashboardHeader
      title={t("project.plan.title")}
      subtitle={t("project.plan.subtitle")}
    >
      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[150px]"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            onClick={handleAddEventFromContextMenu}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("project.event.create")}
          </button>
        </div>
      )}
      <div className="min-h-screen bg-gray-50">
        <div className="space-y-6 mt-10">
          {/* Header Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {t("project.plan.title")}
              </h2>
              <button
                onClick={handleNewEvent}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t("project.event.create")}
              </button>
            </div>

            {/* Project Selection */}
            {!project_id && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("project.plan.select_project")}
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedProject(null);
                      setSearchParams({});
                    }}
                    className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                      !selectedProject
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {t("All Projects")}
                  </button>
                  <div className="flex-1">
                    <ProjectSelector
                      name="project"
                      selected={selectedProject}
                      onChange={(data) => {
                        const project = data?.target?.object;
                        setSelectedProject(project);
                        if (project?.id) {
                          setSearchParams({ project_id: project.id });
                        } else {
                          setSearchParams({});
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Period Selector */}
            <div className="mb-6">
              <PeriodSelector
                periodType={periodType}
                baseDate={baseDate}
                from={dateFrom}
                to={dateTo}
                onChange={handlePeriodChange}
              />
            </div>
          </div>

          {(project_id || selectedProject?.id) && (
            <div className="max-w-5xl">
              <CrewProjectPanel
                projectId={project_id ? parseInt(project_id, 10) : selectedProject?.id}
                selectedDate={baseDate}
              />
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("project.plan.resource_capacity")}
                </h3>
                <p className="text-sm text-gray-500">
                  {t("project.plan.resource_capacity_subtitle")}
                </p>
              </div>
              {resourcePlanLoading ? (
                <span className="text-sm text-gray-500">{t("project.plan.loading_resource_plan")}</span>
              ) : null}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-5">
              <CapacityCard label={t("project.plan.capacity.events")} value={capacityTotals.events} />
              <CapacityCard label={t("project.plan.capacity.available")} value={capacityTotals.available} />
              <CapacityCard label={t("project.plan.capacity.assigned")} value={capacityTotals.assignedResources} />
              <CapacityCard label={t("project.plan.capacity.scheduled_hours")} value={`${capacityTotals.scheduledHours.toFixed(1)}h`} />
              <CapacityCard label={t("project.plan.capacity.free_hours")} value={`${capacityTotals.freeHours.toFixed(1)}h`} />
            </div>

            <div className="mt-4 overflow-x-auto">
              <div className="flex min-w-max gap-2">
                {capacityDays.map((day) => (
                  <div
                    key={day.date}
                    className={`w-36 rounded-md border p-3 text-sm ${
                      day.freeResources > 0 ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
                    }`}
                  >
                    <div className="font-semibold text-gray-900">
                      {format(new Date(`${day.date}T00:00:00`), "dd.MM")}
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      {t("project.plan.capacity.assigned_of_available", {
                        assigned: day.assignedResources,
                        available: day.available,
                      })}
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      {t("project.plan.capacity.free_hours_value", {
                        hours: day.freeHours.toFixed(1),
                      })}
                    </div>
                    {day.events > 0 ? (
                      <div className="mt-2 rounded bg-white/70 px-2 py-1 text-xs font-medium text-gray-700">
                        {t("project.plan.capacity.events_value", { count: day.events })}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Events Table */}
          <div className="bg-white rounded-lg shadow p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-sm text-gray-500">
                  {t("project.plan.loading")}
                </p>
              </div>
            ) : (
              renderPeriodTable()
            )}
          </div>
        </div>

        {/* Event Dialog */}
        <ProjectEventDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setSelectedEvent(null);
            setPrefillDate(null);
          }}
          event={selectedEvent}
          project={selectedProject}
          prefillDate={prefillDate}
          onSaved={handleEventSaved}
        />
      </div>
    </DashboardHeader>
  );
};

const CapacityCard = ({ label, value }) => (
  <div className="rounded-md bg-slate-50 p-3">
    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
  </div>
);

export const ProjectPlan = () => {
  const canAccessProjectPlan = useUserHasAccess({ roles: ["projects_plan"] });

  if (!canAccessProjectPlan) {
    return <Navigate to="/dashboard" replace />;
  }

  return <ProjectPlanContent />;
};

